import React from "react";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

type DashboardScrollRegionProps = {
  ariaLabel: string;
  children: React.ReactNode;
  id?: string;
  maxHeight?: React.CSSProperties["maxHeight"];
};

const SCROLL_STEP = 80;

export default function DashboardScrollRegion({
  ariaLabel,
  children,
  id,
  maxHeight,
}: DashboardScrollRegionProps) {
  const regionRef = React.useRef<HTMLDivElement>(null);
  const generatedRegionId = `dashboard-scroll-region-${React.useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const regionId = id || generatedRegionId;
  const [scrollState, setScrollState] = React.useState({
    canScrollHorizontally: false,
    canScrollVertically: false,
    canScrollDown: false,
    canScrollLeft: false,
    canScrollRight: false,
    canScrollUp: false,
  });

  const updateScrollState = React.useCallback(() => {
    const region = regionRef.current;
    if (!region) {
      return;
    }

    const canScrollVertically = region.scrollHeight > region.clientHeight + 1;
    const canScrollHorizontally = region.scrollWidth > region.clientWidth + 1;
    setScrollState({
      canScrollHorizontally,
      canScrollVertically,
      canScrollDown:
        canScrollVertically &&
        region.scrollTop + region.clientHeight < region.scrollHeight - 1,
      canScrollLeft: canScrollHorizontally && region.scrollLeft > 0,
      canScrollRight:
        canScrollHorizontally &&
        region.scrollLeft + region.clientWidth < region.scrollWidth - 1,
      canScrollUp: canScrollVertically && region.scrollTop > 0,
    });
  }, []);

  React.useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState]);

  React.useEffect(() => {
    updateScrollState();
  }, [children, updateScrollState]);

  const scrollBy = ({ left, top }: { left?: number; top?: number }) => {
    regionRef.current?.scrollBy({ behavior: "smooth", left, top });
  };

  const showScrollControls =
    scrollState.canScrollVertically || scrollState.canScrollHorizontally;

  return (
    <Box
      className="dashboard-scroll-region-shell"
      sx={{
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        maxHeight,
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        paddingBottom: 0,
        paddingRight: 0,
        position: "relative",
        "&:hover .dashboard-scroll-region-controls, &:focus-within .dashboard-scroll-region-controls":
          {
            opacity: 1,
            pointerEvents: "auto",
          },
      }}
    >
      <Box
        aria-label={ariaLabel}
        className="dashboard-scroll-region"
        id={regionId}
        onScroll={updateScrollState}
        ref={regionRef}
        role="region"
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          minWidth: 0,
          overflowX: "auto",
          overflowY: "auto",
          position: "relative",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "&:focus-visible": {
            outline: "3px solid #3E76C3",
            outlineOffset: "-3px",
          },
        }}
        tabIndex={0}
      >
        {children}
      </Box>
      {showScrollControls && (
        <Box
          aria-label={`Scroll ${ariaLabel.toLocaleLowerCase()}`}
          className="dashboard-scroll-region-controls"
          role="group"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            opacity: 0,
            pointerEvents: "none",
            position: "absolute",
            bottom: 8,
            right: 8,
            transition: "opacity 160ms ease-in-out",
            zIndex: 1,
            "& .MuiIconButton-root": {
              backgroundColor: "#FFFFFF",
              border: "1px solid #3E76C3",
              borderRadius: "8px",
              color: "#1F4970",
              height: 44,
              width: 44,
              "&:hover": { backgroundColor: "#EAF6FF" },
              "&:focus-visible": {
                outline: "3px solid #3E76C3",
                outlineOffset: "2px",
              },
              "&.Mui-disabled": {
                backgroundColor: "#F2F6FA",
                borderColor: "#DFE5EF",
                color: "#7C8FAC",
              },
            },
          }}
        >
          {scrollState.canScrollHorizontally && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                aria-controls={regionId}
                aria-label={`Scroll ${ariaLabel.toLocaleLowerCase()} left`}
                disabled={!scrollState.canScrollLeft}
                onClick={() => scrollBy({ left: -SCROLL_STEP })}
                size="small"
                title="Scroll left"
              >
                <KeyboardArrowLeft />
              </IconButton>
              <IconButton
                aria-controls={regionId}
                aria-label={`Scroll ${ariaLabel.toLocaleLowerCase()} right`}
                disabled={!scrollState.canScrollRight}
                onClick={() => scrollBy({ left: SCROLL_STEP })}
                size="small"
                title="Scroll right"
              >
                <KeyboardArrowRight />
              </IconButton>
            </Box>
          )}
          {scrollState.canScrollVertically && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <IconButton
                aria-controls={regionId}
                aria-label={`Scroll ${ariaLabel.toLocaleLowerCase()} up`}
                disabled={!scrollState.canScrollUp}
                onClick={() => scrollBy({ top: -SCROLL_STEP })}
                size="small"
                title="Scroll up"
              >
                <KeyboardArrowUp />
              </IconButton>
              <IconButton
                aria-controls={regionId}
                aria-label={`Scroll ${ariaLabel.toLocaleLowerCase()} down`}
                disabled={!scrollState.canScrollDown}
                onClick={() => scrollBy({ top: SCROLL_STEP })}
                size="small"
                title="Scroll down"
              >
                <KeyboardArrowDown />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
